import { useEffect, useState } from "react";
import { X, Loader2, HelpCircle, Sparkles, ChevronRight, RotateCw, Send, CheckCircle2, CircleDashed, HeartHandshake } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { Textarea } from "./ui/textarea";

const VERDICTS = {
  strong: {
    label: "you got it",
    icon: CheckCircle2,
    color: "text-emerald-600 dark:text-emerald-400",
    ring: "border-emerald-500/40",
    bg: "bg-emerald-50/60 dark:bg-emerald-950/30",
  },
  partial: {
    label: "close — a piece missing",
    icon: CircleDashed,
    color: "text-amber-600 dark:text-amber-400",
    ring: "border-amber-500/40",
    bg: "bg-amber-50/60 dark:bg-amber-950/30",
  },
  off_track: {
    label: "let's look again",
    icon: HeartHandshake,
    color: "text-primary",
    ring: "border-primary/40",
    bg: "bg-primary/5",
  },
};

function QuestionCard({ i, q, hint, bookId, chapterIndex }) {
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revealHint, setRevealHint] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!answer.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/study/answer", {
        book_id: bookId,
        chapter_index: chapterIndex,
        question_index: i,
        question: q,
        answer: answer.trim(),
        hint: hint || null,
      });
      setFeedback(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "couldn't get feedback — try again");
    } finally {
      setLoading(false);
    }
  };

  const tryAgain = () => {
    setFeedback(null);
    setError(null);
  };

  const v = feedback ? VERDICTS[feedback.verdict] || VERDICTS.partial : null;
  const VIcon = v?.icon;

  return (
    <li className="border border-border rounded-md bg-card p-5 paper-shadow" data-testid={`recap-question-${i}`}>
      <div className="flex items-start gap-3">
        <span className="font-display text-2xl text-primary leading-none">{i + 1}.</span>
        <div className="flex-1">
          <div className="font-reader text-base md:text-lg leading-relaxed">{q}</div>

          {hint && !revealHint && !feedback && (
            <button
              onClick={() => setRevealHint(true)}
              data-testid={`reveal-hint-${i}`}
              className="mt-3 inline-flex items-center gap-1.5 font-ui text-[10px] tracking-caps text-muted-foreground hover:text-primary"
            >
              <HelpCircle className="w-3 h-3" /> reveal hint
            </button>
          )}
          {hint && revealHint && !feedback && (
            <div className="mt-3 font-reader italic text-sm text-muted-foreground border-l-2 border-primary/40 pl-3">
              {hint}
            </div>
          )}

          {!feedback && (
            <div className="mt-4">
              <Textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="write your answer in your own words…"
                data-testid={`answer-input-${i}`}
                className="min-h-[90px] font-reader text-base"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="font-ui text-[10px] tracking-caps text-muted-foreground">
                  {answer.trim().length} characters
                </span>
                <button
                  onClick={submit}
                  disabled={loading || !answer.trim()}
                  data-testid={`submit-answer-${i}`}
                  className="inline-flex items-center gap-1.5 font-ui text-xs bg-primary text-primary-foreground px-4 py-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {loading ? "reading it…" : "check my answer"}
                </button>
              </div>
              {error && <div className="mt-2 font-ui text-xs text-destructive">{error}</div>}
            </div>
          )}

          <AnimatePresence>
            {feedback && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                data-testid={`answer-feedback-${i}`}
                className={`mt-4 rounded-md border ${v.ring} ${v.bg} p-4`}
              >
                <div className={`flex items-center gap-2 font-ui text-xs tracking-caps ${v.color} mb-2`}>
                  {VIcon && <VIcon className="w-4 h-4" />} {v.label}
                </div>
                <div className="font-reader text-sm md:text-base leading-relaxed">{feedback.feedback}</div>
                {feedback.nudge && (
                  <div className="mt-3 font-reader italic text-sm text-muted-foreground border-l-2 border-primary/40 pl-3">
                    {feedback.nudge}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between">
                  <div className="font-ui italic text-xs text-muted-foreground line-clamp-1">
                    your answer: &ldquo;{answer}&rdquo;
                  </div>
                  <button
                    onClick={tryAgain}
                    data-testid={`retry-answer-${i}`}
                    className="font-ui text-[10px] tracking-caps text-muted-foreground hover:text-primary"
                  >
                    <RotateCw className="inline w-3 h-3 mr-1" /> answer again
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </li>
  );
}

/**
 * Study Mode chapter recap panel — full-screen overlay that shows when the reader
 * reaches the last page of a chapter (or hits "Recap chapter" manually).
 *
 * Props: { bookId, chapterIndex, chapterTitle, onClose, onNextChapter }
 */
export default function StudyRecap({ bookId, chapterIndex, chapterTitle, onClose, onNextChapter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchRecap = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.post("/study/recap", {
        book_id: bookId,
        chapter_index: chapterIndex,
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "could not generate a recap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecap(); /* eslint-disable-next-line */ }, [bookId, chapterIndex]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md overflow-y-auto"
      data-testid="study-recap-overlay"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14 pb-24">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2 font-ui text-xs tracking-caps text-primary">
            <Sparkles className="w-3.5 h-3.5" /> study mode · chapter recap
          </div>
          <button onClick={onClose} data-testid="close-recap-btn" className="p-2 rounded-full hover:bg-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="py-24 text-center font-ui text-sm text-muted-foreground">
            <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-primary" />
            reading over the chapter for you…
          </div>
        )}

        {err && !loading && (
          <div className="py-24 text-center">
            <div className="font-reader italic text-muted-foreground mb-4">{err}</div>
            <button
              onClick={fetchRecap}
              data-testid="retry-recap-btn"
              className="inline-flex items-center gap-2 font-ui text-xs tracking-caps px-4 py-2 rounded-full border border-border hover:bg-secondary"
            >
              <RotateCw className="w-3.5 h-3.5" /> try again
            </button>
          </div>
        )}

        {data && !loading && (
          <>
            <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight" data-testid="recap-chapter-title">
              {chapterTitle}
            </h2>

            {data.themes?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="recap-themes">
                {data.themes.map((t, i) => (
                  <span key={i} className="font-ui text-[10px] tracking-caps px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground">
                    {t}
                  </span>
                ))}
              </div>
            )}

            <p className="mt-8 font-reader text-lg md:text-xl leading-[1.75]" data-testid="recap-body">
              {data.recap}
            </p>

            {data.questions?.length > 0 && (
              <div className="mt-14">
                <div className="rule-ornament font-ui text-xs tracking-caps mb-6">
                  <span>reflect · {data.questions.length} questions · answer to check yourself</span>
                </div>
                <ol className="space-y-5" data-testid="recap-questions">
                  {data.questions.map((qq, i) => (
                    <QuestionCard
                      key={i}
                      i={i}
                      q={qq.q}
                      hint={qq.hint}
                      bookId={bookId}
                      chapterIndex={chapterIndex}
                    />
                  ))}
                </ol>
              </div>
            )}

            <div className="mt-14 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={onClose}
                data-testid="keep-reading-btn"
                className="font-ui text-sm px-6 py-2.5 rounded-full border border-border hover:bg-secondary transition-colors"
              >
                back to reading
              </button>
              {onNextChapter && (
                <button
                  onClick={onNextChapter}
                  data-testid="next-chapter-btn"
                  className="inline-flex items-center gap-2 font-ui text-sm bg-primary text-primary-foreground px-6 py-2.5 rounded-full hover:opacity-90 transition-opacity"
                >
                  next chapter <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
